import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  MapPin, 
  Calendar, 
  Thermometer, 
  Home, 
  Gem, 
  Users,
  Mountain,
  Sun,
  Moon,
  Building,
  Church,
  ShoppingBag,
  Tent,
  ArrowRight,
  Wrench,
  Film,
  Tv,
  Clapperboard
} from "lucide-react";

const CooberPedyPage = () => {
  // Load Vadoo player scripts for embedded videos
  useEffect(() => {
    const videos = [
      { id: 'tLdL0W0Qze8c2KwayjRxem5CxuyACsVc', containerId: 'video-1' },
      { id: '8m3Tf3igiKbJtptY3xxPTYSAHHY9X80A', containerId: 'video-2' },
      { id: '663tHYBXD5tLX7cPT2qk7AyHI5TTWSm6', containerId: 'video-3' }
    ];

    const scriptsLoaded = [];

    videos.forEach((video) => {
      // Create and append script for each video
      const script = document.createElement('script');
      script.src = 'https://api.vadoo.tv/static/vadoo_player.min.js';
      script.id = `player_script_${video.containerId}`;
      script.setAttribute('vid', video.id);
      script.async = false; // Load sequentially
      
      script.onload = () => {
        console.log(`Vadoo player script loaded for ${video.containerId}`);
      };
      
      document.body.appendChild(script);
      scriptsLoaded.push(script);
    });

    return () => {
      // Cleanup scripts on unmount
      scriptsLoaded.forEach(script => {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []);

  const timelineEvents = [
    {
      year: "1915",
      title: "Discovery of Opal",
      description: "Willie Hutchison, a 14-year-old boy, discovered opal while sheltering from a sandstorm. This discovery would change the region forever."
    },
    {
      year: "1920s",
      title: "Mining Rush Begins",
      description: "Word spread about the opal finds, and miners from around Australia began arriving to stake their claims."
    },
    {
      year: "1960s-1970s",
      title: "Underground Living",
      description: "As the town grew, residents began building underground homes (dugouts) to escape the extreme heat. This became Coober Pedy's signature feature."
    },
    {
      year: "Today",
      title: "World's Opal Capital",
      description: "Coober Pedy produces over 70% of the world's precious opal and remains a unique outback town with a population of around 2,500 people."
    }
  ];

  const facts = [
    {
      icon: Thermometer,
      title: "Extreme Climate",
      description: "Summer temperatures can reach 50°C (122°F), making underground living essential for comfort."
    },
    {
      icon: Home,
      title: "Underground Living",
      description: "Over 1,500 residents live in underground dugouts, maintaining a constant temperature of 22-24°C year-round."
    },
    {
      icon: Gem,
      title: "Opal Capital",
      description: "Produces 70% of the world's precious opal, with mining still the primary industry."
    },
    {
      icon: Users,
      title: "Multicultural Community",
      description: "Over 45 nationalities call Coober Pedy home, creating a unique multicultural outback community."
    },
    {
      icon: Church,
      title: "Underground Churches",
      description: "Features underground churches, including the Serbian Orthodox Church and the Catholic Catacomb Church."
    },
    {
      icon: Building,
      title: "Underground Businesses",
      description: "Hotels, shops, and museums operate underground, including the famous Desert Cave Hotel."
    }
  ];

  const lifestyle = [
    {
      title: "Housing & Living",
      description: "Most homes are carved directly into hillsides. These 'dugouts' stay naturally cool in summer and warm in winter, eliminating the need for air conditioning or heating."
    },
    {
      title: "Mining Today",
      description: "While large-scale mining has decreased, many residents still mine for opal. The town has strict mining regulations to preserve the landscape and ensure safety."
    },
    {
      title: "Tourism",
      description: "Tourism has become increasingly important, with visitors coming to experience underground living, visit opal mines, and explore the unique desert landscape."
    },
    {
      title: "Community Life",
      description: "Despite its remote location (850km north of Adelaide), Coober Pedy has schools, medical facilities, shops, and a strong sense of community."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-purple-50">
      <SEOHead 
        title="Coober Pedy - Australia's Underground Town | Umoona Opal Mine"
        description="Discover Coober Pedy, the world's opal capital. Learn about the history, underground living, and unique lifestyle of this remarkable outback town."
        keywords="Coober Pedy, underground town, opal capital, outback Australia, dugout homes, South Australia"
        ogImage="/og-umoona.jpg"
        url="https://umoonaopalmine.com.au/coober-pedy"
      />

      {/* Hero Section - Clean & Elegant */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/assets/2019-3170.jpg"
            alt="Coober Pedy - Australia's Underground Town"
            className="w-full h-full object-cover scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/70"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-orange-900/20 to-transparent"></div>
        </div>
        
        {/* Animated particles effect */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-orange-400/30 rounded-full animate-pulse"></div>
          <div className="absolute top-3/4 right-1/3 w-1 h-1 bg-yellow-300/40 rounded-full animate-ping"></div>
          <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-orange-300/20 rounded-full animate-pulse"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 z-10">
          <div className="text-center max-w-5xl mx-auto">
            <div className="space-y-8 md:space-y-12">
              
              {/* Pre-heading Badge */}
              <div className="space-y-6">
                <Badge variant="secondary" className="bg-orange-500/20 text-orange-300 border border-orange-400/30 hover:bg-orange-500/30 px-4 md:px-6 py-2 md:py-3 text-sm md:text-lg backdrop-blur-sm animate-fade-in">
                  💎 World's Opal Capital
                </Badge>
                
                {/* Main heading with dramatic effect */}
                <div className="space-y-4">
                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black text-white leading-none tracking-tight" style={{ fontFamily: "'Exo', sans-serif" }}>
                    <span className="block animate-slide-up-1">DISCOVER</span>
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-yellow-300 to-orange-500 animate-slide-up-2">
                      COOBER PEDY
                    </span>
                    <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-4 animate-slide-up-3">
                      Australia's Underground Town
                    </span>
                  </h1>
                </div>
                
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 leading-relaxed max-w-4xl mx-auto animate-fade-in-delayed px-4">
                  Experience the <span className="text-orange-300 font-semibold">world's opal capital</span> and one of the most unique towns on Earth. 
                  Journey through <span className="text-orange-300 font-semibold">underground living</span>, 
                  explore stunning landscapes, and discover a <span className="text-orange-300 font-semibold">multicultural community</span> thriving in the outback.
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center animate-fade-in-delayed-2">
                <Button 
                  asChild
                  size="lg" 
                  className="mining-cta font-bold px-8 md:px-10 py-4 md:py-5 text-base md:text-xl rounded-xl shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  <Link to="/underground-tours">
                    ⛏️ Underground Tours
                    <ArrowRight className="ml-2 md:ml-3 w-5 h-5 md:w-6 md:h-6" />
                  </Link>
                </Button>
                <Button 
                  asChild
                  variant="outline" 
                  size="lg"
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-8 md:px-10 py-4 md:py-5 text-base md:text-xl rounded-xl backdrop-blur-sm shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  <Link to="/free-museum">
                    🏛️ Free Museum
                  </Link>
                </Button>
              </div>

              {/* Impact Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 pt-8 md:pt-12 animate-fade-in-delayed-3">
                <div className="bg-white/10 backdrop-blur-sm p-6 md:p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">2,500+</div>
                  <div className="text-white/80 text-sm md:text-base uppercase tracking-wider">Residents</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 md:p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">1,500+</div>
                  <div className="text-white/80 text-sm md:text-base uppercase tracking-wider">Underground Homes</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 md:p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">45+</div>
                  <div className="text-white/80 text-sm md:text-base uppercase tracking-wider">Nationalities</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 heritage-heading">
            Welcome to the Opal Capital of the World
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-4">
            Coober Pedy is like nowhere else on Earth. This remarkable outback town, located 850 kilometers north of Adelaide in the heart of South Australia's desert, is famous for its underground living and world-class opals.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            The name "Coober Pedy" comes from the Aboriginal term "kupa-piti," meaning "white man's hole" – a reference to the underground dugout homes that make this town so unique.
          </p>
        </div>

        {/* Photo Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          <div className="md:col-span-2 relative rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/assets/2019-3170.jpg"
              alt="Coober Pedy landscape"
              className="w-full h-80 object-cover"
            />
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/assets/2021--4.jpg"
              alt="Coober Pedy town view"
              className="w-full h-80 object-cover"
            />
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/assets/2019-3206.jpg"
              alt="Underground opal mining"
              className="w-full h-64 object-cover"
            />
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/assets/2021-0008.jpg"
              alt="Coober Pedy mining equipment"
              className="w-full h-64 object-cover"
            />
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/assets/2021--3.jpg"
              alt="Coober Pedy underground"
              className="w-full h-64 object-cover"
            />
          </div>
        </div>

        {/* Video Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Watch: Life in Coober Pedy
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience authentic stories from local miners and residents who call Coober Pedy home
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Video 1 - Mining with George and Yannie */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="aspect-video bg-gray-100">
                <div 
                  id="video-1"
                  className="vadoo_player" 
                  vid="tLdL0W0Qze8c2KwayjRxem5CxuyACsVc" 
                  etype="responsive"
                  style={{ width: '100%', height: '100%' }}
                ></div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Mining with George and Yannie</h3>
                <p className="text-gray-600">Follow local miners George and Yannie as they search for precious opals in the fields of Coober Pedy</p>
              </div>
            </div>

            {/* Video 2 - Noodling with Veronika */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="aspect-video bg-gray-100">
                <div 
                  id="video-2"
                  className="vadoo_player" 
                  vid="8m3Tf3igiKbJtptY3xxPTYSAHHY9X80A" 
                  etype="responsive"
                  style={{ width: '100%', height: '100%' }}
                ></div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Noodling with Veronika</h3>
                <p className="text-gray-600">Join Veronika as she demonstrates the traditional art of noodling - searching through mining mullock for opals</p>
              </div>
            </div>

            {/* Video 3 - The Bomb King */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="aspect-video bg-gray-100">
                <div 
                  id="video-3"
                  className="vadoo_player" 
                  vid="663tHYBXD5tLX7cPT2qk7AyHI5TTWSm6" 
                  etype="responsive"
                  style={{ width: '100%', height: '100%' }}
                ></div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">The Bomb King</h3>
                <p className="text-gray-600">Meet the legendary "Bomb King" and learn about explosive mining techniques in Coober Pedy's history</p>
              </div>
            </div>
          </div>
        </div>

        {/* The Spirit of Opal Miners */}
        <div className="mb-20">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 md:p-12 border-2 border-orange-200">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-6 heritage-heading">
              The Spirit of Coober Pedy's Opal Miners
            </h2>
            <p className="text-xl text-gray-700 text-center max-w-4xl mx-auto mb-12 leading-relaxed">
              The story of Coober Pedy is the story of determination, resilience, and the pioneering spirit 
              of opal miners who transformed a harsh desert into a thriving community through sheer hard work and ingenuity.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Building a Town */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                    <Wrench className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Building a Town</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  From the 1920s onward, opal miners worked tirelessly in extreme conditions. Working by hand in 
                  scorching heat that often exceeded 50°C, these pioneers dug into the earth with picks, shovels, 
                  and unwavering determination. Days were long, the work was backbreaking, and success was never guaranteed 
                  – yet they persevered, driven by the dream of finding that life-changing opal.
                </p>
              </div>

              {/* Underground Innovation */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Underground Innovation</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  To escape the brutal desert heat, miners carved homes directly into the hillsides, creating the 
                  unique dugout dwellings that define Coober Pedy today. This wasn't just about comfort – it was 
                  survival. These underground homes stayed naturally cool at 22-24°C year-round, allowing families 
                  to live and thrive in one of Earth's harshest environments. This innovative solution showcases 
                  the resourcefulness and adaptability of the mining community.
                </p>
              </div>

              {/* A Global Community */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">A Global Community</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Miners from over 45 countries came to Coober Pedy seeking their fortune, bringing with them diverse 
                  cultures, languages, and traditions. Greeks, Italians, Yugoslavians, Germans, and people from across 
                  the globe worked side-by-side in the opal fields. This multicultural heritage shaped Coober Pedy into 
                  a truly unique Australian town, where different cultures blended while maintaining their distinct identities. 
                  Today, underground churches from various denominations stand as testament to this diversity.
                </p>
              </div>

              {/* The Legacy Lives On */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-lg flex items-center justify-center">
                    <Gem className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">The Legacy Lives On</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Today's opal mining families, including those at Umoona Opal Mine, carry forward this remarkable legacy. 
                  While modern equipment has replaced some of the hand tools, the spirit remains the same – a deep respect 
                  for the land, dedication to the craft, and pride in sharing authentic Australian opals with the world. 
                  These mining families don't just sell opals; they share stories, knowledge, and a century of mining heritage 
                  that makes Coober Pedy truly special.
                </p>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl border-2 border-orange-300">
              <p className="text-center text-gray-800 text-lg leading-relaxed">
                <strong>The miners of Coober Pedy didn't just dig for opals – they built a town, created a community, 
                and established a way of life that continues to this day.</strong> Their hard work, innovation, and 
                determination transformed a remote desert into the Opal Capital of the World.
              </p>
            </div>
          </div>
        </div>

        {/* Key Facts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {facts.map((fact, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-orange-100"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                <fact.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{fact.title}</h3>
              <p className="text-gray-600 leading-relaxed">{fact.description}</p>
            </div>
          ))}
        </div>

        {/* History Timeline */}
        <div className="mb-20">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12 heritage-heading">
            A Brief History of Coober Pedy
          </h2>
          
          <div className="max-w-4xl mx-auto">
            {timelineEvents.map((event, index) => (
              <div 
                key={index}
                className="relative pl-8 pb-12 border-l-4 border-orange-500 last:pb-0"
              >
                <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-orange-500 border-4 border-white shadow"></div>
                <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-5 h-5 text-orange-600" />
                    <span className="text-2xl font-bold text-orange-600">{event.year}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Life in Coober Pedy Today */}
        <div className="bg-gradient-to-br from-orange-50 to-purple-50 rounded-3xl p-8 md:p-12 mb-20">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12 heritage-heading">
            Life in Coober Pedy Today
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {lifestyle.map((item, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 shadow-md"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <ArrowRight className="w-6 h-6 text-orange-600" />
                  {item.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Climate Section */}
        <div className="mb-20">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-8 heritage-heading">
              The Extreme Climate
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="text-center p-6 bg-gradient-to-br from-orange-100 to-orange-50 rounded-xl">
                <Sun className="w-16 h-16 text-orange-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Summer</h3>
                <p className="text-4xl font-bold text-orange-600 mb-2">50°C</p>
                <p className="text-gray-600">December - February</p>
                <p className="text-sm text-gray-500 mt-2">One of Australia's hottest locations</p>
              </div>
              
              <div className="text-center p-6 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl">
                <Moon className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Winter</h3>
                <p className="text-4xl font-bold text-blue-600 mb-2">7°C</p>
                <p className="text-gray-600">June - August</p>
                <p className="text-sm text-gray-500 mt-2">Cold desert nights</p>
              </div>
            </div>
            
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                The extreme temperature variations and minimal rainfall (averaging only 175mm per year) make Coober Pedy one of the harshest environments in Australia. This is precisely why underground living became not just practical, but essential.
              </p>
              <p className="text-gray-600">
                Underground homes maintain a comfortable 22-24°C year-round without heating or cooling systems, making them both comfortable and energy-efficient.
              </p>
            </div>
          </div>
        </div>

        {/* Nearby Attractions */}
        <div className="mb-20">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12 heritage-heading">
            Explore the Surrounding Area
          </h2>
          
          <div className="space-y-12">
            {/* The Dog Fence */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 md:p-12 border-2 border-orange-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                  <Mountain className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">The Dog Fence</h3>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  Just outside Coober Pedy, you'll find one of the longest man-made structures in the world – the Dog Fence (also known as the Dingo Fence). Stretching an incredible <strong>5,614 kilometers</strong> across Australia, this fence runs through South Australia, Queensland, and New South Wales.
                </p>
                <p>
                  Built in the 1880s and continuously maintained, the Dog Fence was designed to keep dingoes away from fertile southern grazing lands and protect livestock. The section near Coober Pedy offers visitors a unique opportunity to see this engineering marvel and understand its importance to Australia's pastoral industry.
                </p>
                <p className="italic text-gray-600">
                  At over 5,600km long, the Dog Fence is longer than the Great Wall of China and represents one of Australia's most ambitious infrastructure projects.
                </p>
              </div>
            </div>

            {/* Kanku-Breakaways Conservation Park */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 md:p-12 border-2 border-purple-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                  <Mountain className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Kanku-Breakaways Conservation Park</h3>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  Located just 33 kilometers north of Coober Pedy, the Kanku-Breakaways Conservation Park is a stunning natural landscape featuring colorful rocky hills, ancient geological formations, and breathtaking desert vistas. The name "Breakaways" refers to the way the hills appear to have broken away from the Stuart Range.
                </p>
                <p>
                  This spectacular landscape showcases layers of geological history spanning millions of years. The vibrant colors – ranging from deep reds and purples to yellows and whites – are especially dramatic at sunrise and sunset. The area was once an ancient inland sea, and the sedimentary layers tell the story of Earth's prehistoric past.
                </p>
                <p>
                  <strong>Movie Location:</strong> The unique otherworldly landscape has made it a popular filming location, including scenes from the Mad Max films and "The Adventures of Priscilla, Queen of the Desert."
                </p>
                <div className="bg-white/60 p-4 rounded-xl">
                  <p className="font-semibold text-purple-900 mb-2">Visitor Information:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Distance: 33km north of Coober Pedy</li>
                    <li>• Best Time: Sunrise or sunset for spectacular colors</li>
                    <li>• Self-guided scenic drive with lookout points</li>
                    <li>• Aboriginal cultural significance (Antakirinja Matu-Yankunytjatjara people)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* The Moon Plain */}
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-8 md:p-12 border-2 border-slate-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-slate-500 to-blue-600 rounded-full flex items-center justify-center">
                  <Moon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">The Moon Plain</h3>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  Coober Pedy's landscape is so otherworldly and desolate that it's often compared to the surface of the moon. The Moon Plain, located on the outskirts of town, features a stark, crater-like landscape dotted with thousands of opal mining mullock heaps (piles of excavated earth) that create an alien, lunar-like appearance.
                </p>
                <p>
                  This unique terrain is the result of over 100 years of opal mining activity. The mullock heaps – white, grey, and ochre mounds of earth brought up from underground mines – have transformed the landscape into something that looks more like an extraterrestrial planet than Earth. The resemblance to the moon's surface is so striking that NASA has used the area for testing lunar vehicles and equipment.
                </p>
                <p>
                  <strong>Film & Media:</strong> The Moon Plain's distinctive appearance has made it a sought-after filming location for science fiction movies and post-apocalyptic scenes, perfectly capturing an otherworldly atmosphere without special effects.
                </p>
                <div className="bg-white/60 p-4 rounded-xl">
                  <p className="font-semibold text-slate-900 mb-2">Did You Know?</p>
                  <p className="text-gray-700">
                    The lunar-like landscape is entirely man-made, created by generations of miners extracting opal from deep underground. Each mullock heap represents one person's dream of finding that life-changing opal.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Movies and TV Shows */}
        <div className="mb-20">
          <div className="bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Film className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-center">
                Coober Pedy on Screen
              </h2>
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Clapperboard className="w-8 h-8 text-white" />
              </div>
            </div>
            
            <p className="text-xl text-center mb-12 max-w-4xl mx-auto text-purple-100">
              Coober Pedy's otherworldly landscape has made it a favorite location for filmmakers from around the world. The town's unique underground dwellings, lunar-like terrain, and dramatic desert vistas have provided the perfect backdrop for countless productions.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Major Films */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center gap-3 mb-4">
                  <Film className="w-6 h-6 text-yellow-400" />
                  <h3 className="text-2xl font-bold">Major Films</h3>
                </div>
                <ul className="space-y-4">
                  <li>
                    <div className="font-bold text-lg text-yellow-300">Mad Max Beyond Thunderdome (1985)</div>
                    <p className="text-purple-100 text-sm">Starring Mel Gibson and Tina Turner. The iconic post-apocalyptic landscape scenes were filmed in and around Coober Pedy, perfectly capturing the barren, otherworldly environment.</p>
                  </li>
                  <li>
                    <div className="font-bold text-lg text-yellow-300">The Adventures of Priscilla, Queen of the Desert (1994)</div>
                    <p className="text-purple-100 text-sm">This beloved Australian film featured Coober Pedy's unique character and underground hotel. The town's quirky charm and stunning Breakaways were key to the film's visual impact.</p>
                  </li>
                  <li>
                    <div className="font-bold text-lg text-yellow-300">Red Planet (2000)</div>
                    <p className="text-purple-100 text-sm">Starring Val Kilmer. The Moon Plain and surrounding areas were used to depict the surface of Mars, requiring minimal special effects due to the landscape's natural resemblance to another planet.</p>
                  </li>
                  <li>
                    <div className="font-bold text-lg text-yellow-300">Pitch Black (2000)</div>
                    <p className="text-purple-100 text-sm">Vin Diesel's breakout film used Coober Pedy's dramatic desert terrain for this sci-fi thriller about a hostile alien planet.</p>
                  </li>
                  <li>
                    <div className="font-bold text-lg text-yellow-300">Until the End of the World (1991)</div>
                    <p className="text-purple-100 text-sm">Directed by Wim Wenders, this epic sci-fi road movie featured Coober Pedy's underground churches and unique architecture.</p>
                  </li>
                </ul>
              </div>

              {/* TV Shows & Documentaries */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center gap-3 mb-4">
                  <Tv className="w-6 h-6 text-yellow-400" />
                  <h3 className="text-2xl font-bold">TV Shows & Documentaries</h3>
                </div>
                <ul className="space-y-4">
                  <li>
                    <div className="font-bold text-lg text-yellow-300">Doctor Who (2013)</div>
                    <p className="text-purple-100 text-sm">"The Angels Take Manhattan" - Featured Coober Pedy as an alien planet location, taking advantage of the Moon Plain's extraterrestrial appearance.</p>
                  </li>
                  <li>
                    <div className="font-bold text-lg text-yellow-300">The Amazing Race</div>
                    <p className="text-purple-100 text-sm">Multiple international versions have featured Coober Pedy as a challenging and visually stunning destination, showcasing opal mining and underground living to millions of viewers worldwide.</p>
                  </li>
                  <li>
                    <div className="font-bold text-lg text-yellow-300">Outback Opal Hunters</div>
                    <p className="text-purple-100 text-sm">Australian reality TV series that follows modern-day opal miners in Coober Pedy, documenting the challenges and excitement of searching for precious opals.</p>
                  </li>
                  <li>
                    <div className="font-bold text-lg text-yellow-300">Numerous International Documentaries</div>
                    <p className="text-purple-100 text-sm">National Geographic, BBC, and other networks have produced documentaries about Coober Pedy's unique underground lifestyle, mining culture, and geological wonders.</p>
                  </li>
                </ul>
              </div>
            </div>

            {/* Why Filmmakers Love Coober Pedy */}
            <div className="bg-gradient-to-r from-pink-600/40 to-purple-600/40 backdrop-blur-sm rounded-2xl p-8 border border-white/30">
              <h3 className="text-2xl font-bold mb-6 text-center">Why Filmmakers Love Coober Pedy</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mountain className="w-6 h-6 text-purple-900" />
                  </div>
                  <h4 className="font-bold mb-2">Unique Landscape</h4>
                  <p className="text-purple-100 text-sm">The lunar-like terrain requires minimal set dressing or CGI to portray alien planets or post-apocalyptic worlds.</p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Home className="w-6 h-6 text-purple-900" />
                  </div>
                  <h4 className="font-bold mb-2">Underground Architecture</h4>
                  <p className="text-purple-100 text-sm">Authentic underground homes, churches, and hotels provide ready-made sets with unique visual appeal.</p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Sun className="w-6 h-6 text-purple-900" />
                  </div>
                  <h4 className="font-bold mb-2">Natural Lighting</h4>
                  <p className="text-purple-100 text-sm">The extreme outback sun and clear skies provide dramatic natural lighting perfect for cinematic shots.</p>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-lg text-purple-100 italic">
                When you visit Coober Pedy, you're walking through sets that have appeared on screens around the world – from Hollywood blockbusters to beloved international TV shows.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-orange-600 to-purple-600 rounded-3xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Experience Coober Pedy for Yourself
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Visit Umoona Opal Mine & Museum to explore underground living, discover how opals are formed, and experience the unique heritage of this remarkable town.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/underground-tours">
              <button className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-4 rounded-xl font-semibold text-lg transition-colors shadow-lg">
                Book Underground Tour
              </button>
            </Link>
            <Link to="/free-museum">
              <button className="bg-orange-800 hover:bg-orange-900 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors">
                Visit Free Museum
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CooberPedyPage;
